import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const TailwindTable = ({
  columns,
  data,
  page,
  setPage,
  pageSize,
  setPageSize,
  totalItems,
  renderRowActions,
  isLoading = false
}) => {
  // Calcular o número total de páginas
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // Dados paginados
  const displayedData = data.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2"></div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.field}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                  style={{ width: column.width || 'auto' }}
                >
                  {column.headerName}
                </th>
              ))}
              {renderRowActions && (
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Ações
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length + (renderRowActions ? 1 : 0)} className="px-4 py-4 text-center">
                  <div className="flex justify-center">
                    <svg className="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                </td>
              </tr>
            ) : displayedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (renderRowActions ? 1 : 0)} className="px-4 py-4 text-center text-sm text-muted-foreground">
                  Nenhum registro encontrado
                </td>
              </tr>
            ) : (
              displayedData.map((row) => (
                <tr key={row.id} className="hover:bg-muted/50">
                  {columns.map((column) => (
                    <td key={`${row.id}-${column.field}`} className="px-4 py-4">
                      {column.renderCell ? column.renderCell({ row }) : row[column.field]}
                    </td>
                  ))}
                  {renderRowActions && (
                    <td className="px-4 py-4 whitespace-nowrap">
                      {renderRowActions(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Paginação */}
      <div className="flex items-center justify-between px-4 py-3 bg-background border-t">
        <div className="flex-1 text-sm text-muted-foreground">
          Mostrando {page * pageSize + 1} a {Math.min((page + 1) * pageSize, totalItems)} de {totalItems} resultados
        </div>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Linhas por página</p>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {[5, 10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-1 text-sm font-medium hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-1 text-sm font-medium hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm">
              Página {page + 1} de {Math.max(1, totalPages)}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-1 text-sm font-medium hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background p-1 text-sm font-medium hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TailwindTable;